import { type FC, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { ArrowOutwardRounded, LogoutRounded } from "@mui/icons-material";
import {
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { UserAvatar } from "@/partner/shared/ui";
import { PagePaths } from "@/partner/shared/constants";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";
import type { UserType } from "@/types/graphql/partner";

export interface AccountMenuProps {
  user: UserType;
  onSignOut: () => void;
  loading: boolean;
}

export const AccountMenu: FC<AccountMenuProps> = ({
  user,
  onSignOut,
  loading,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isLg = useDeviceBreakpoint("lg");

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    handleCloseMenu();
  };

  return (
    <>
      <Tooltip
        title={t("shared.accountMenu.tooltip", { name: user.displayName })}
      >
        <IconButton component={Link} to={PagePaths.userSettings}>
          <UserAvatar
            userId={user.id}
            avatarUrl={user.avatar?.url}
            size={32}
            sx={{ bgcolor: "primary.main" }}
          />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl && isLg}
        onClose={handleCloseMenu}
        onClick={handleCloseMenu}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => handleNavigate(PagePaths.userSettings)}
          sx={{ display: "flex", gap: 2 }}
        >
          <UserAvatar
            userId={user.id}
            avatarUrl={user.avatar?.url}
            size={32}
            sx={{ bgcolor: "primary.main" }}
          />
          <Stack>
            <Typography color="textPrimary">{user.displayName}</Typography>
            <Typography color="textSecondary">{user.email}</Typography>
          </Stack>
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => handleNavigate(PagePaths.userSettings)}>
          <ListItemIcon
            sx={({ palette }) => ({ color: palette.text.secondary })}
          >
            <ArrowOutwardRounded fontSize="small" />
          </ListItemIcon>
          {t("shared.accountMenu.goToAccount")}
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={onSignOut}
          sx={({ palette }) => ({ color: palette.error.main })}
          disabled={loading}
        >
          <ListItemIcon>
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              <LogoutRounded fontSize="small" color="error" />
            )}
          </ListItemIcon>
          {t("shared.accountMenu.signOut")}
        </MenuItem>
      </Menu>
    </>
  );
};
